import React from 'react';
import { Button } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { WishlistAnalyticsResponse } from '../../services/wishlist-analyzer';

interface WishlistPdfExportProps {
  data: WishlistAnalyticsResponse | null;
  startDate: Date | null;
  endDate: Date | null;
  chart1Ref: React.RefObject<HTMLDivElement>;
  chart2Ref: React.RefObject<HTMLDivElement>;
  chart3Ref: React.RefObject<HTMLDivElement>;
  chart4Ref: React.RefObject<HTMLDivElement>;
  loading?: boolean;
  formatDateToYYYYMMDD: (date: Date) => string;
  fetchAllData?: () => Promise<WishlistAnalyticsResponse | null>;
  selectedPossessionType?: any | null;
  selectedLocation?: any | null;
  selectedCategory?: any | null;
  selectedSubCategory?: any | null;
  searchText?: string;
}

const WishlistPdfExport: React.FC<WishlistPdfExportProps> = ({
  data,
  startDate,
  endDate,
  chart1Ref,
  chart2Ref,
  chart3Ref,
  chart4Ref,
  loading = false,
  formatDateToYYYYMMDD,
  fetchAllData,
  selectedPossessionType = null,
  selectedLocation = null,
  selectedCategory = null,
  selectedSubCategory = null,
  searchText = '',
}) => {
  const [exporting, setExporting] = React.useState<boolean>(false);

  const handleExportPDF = async () => {
    if (!data) return;

    setExporting(true);
    try {
      // Fetch all data for PDF export (not just current page)
      let allWishlistsData = data.data;
      let totalCount = data.total;
      if (fetchAllData) {
        try {
          const allData = await fetchAllData();
          if (allData && allData.data && allData.data.length > 0) {
            allWishlistsData = allData.data;
            totalCount = allData.total;
          }
        } catch (error) {
          console.error('Error fetching all data for PDF:', error);
          // Fall back to current page data if fetch fails
        }
      }

      const pdf = new jsPDF('l', 'mm', 'a4'); // 'l' for landscape orientation
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;

      // Helper function to add a new page if needed
      const checkPageBreak = (requiredHeight: number) => {
        if (yPosition + requiredHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Add Title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Wishlist Analytics Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Add Date Range
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      const dateRangeText = `Date Range: ${startDate ? formatDateToYYYYMMDD(startDate) : 'N/A'} to ${endDate ? formatDateToYYYYMMDD(endDate) : 'N/A'}`;
      pdf.text(dateRangeText, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Add Filter Details (if any filters are applied)
      const hasFilters = selectedPossessionType || selectedLocation || selectedCategory || selectedSubCategory || (searchText && searchText.trim());
      if (hasFilters) {
        checkPageBreak(30);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Applied Filters', margin, yPosition);
        yPosition += 8;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        let leftFilterY = yPosition;
        let rightFilterY = yPosition;
        const lineHeight = 6;
        const leftColumnX = margin;
        const rightColumnX = pageWidth / 2 + 10;

        // Left column filters
        if (selectedPossessionType) {
          pdf.text(`Possession Type: ${selectedPossessionType.labelEn || selectedPossessionType.name || 'N/A'}`, leftColumnX, leftFilterY);
          leftFilterY += lineHeight;
        }
        if (selectedLocation) {
          pdf.text(`Location: ${selectedLocation.labelEn || selectedLocation.name || 'N/A'}`, leftColumnX, leftFilterY);
          leftFilterY += lineHeight;
        }
        if (selectedCategory) {
          pdf.text(`Category: ${selectedCategory.labelEn || selectedCategory.name || 'N/A'}`, leftColumnX, leftFilterY);
          leftFilterY += lineHeight;
        }

        // Right column filters
        if (selectedSubCategory) {
          pdf.text(`Sub Category: ${selectedSubCategory.labelEn || selectedSubCategory.name || 'N/A'}`, rightColumnX, rightFilterY);
          rightFilterY += lineHeight;
        }
        if (searchText && searchText.trim()) {
          pdf.text(`Search: ${searchText.trim()}`, rightColumnX, rightFilterY);
          rightFilterY += lineHeight;
        }

        // Update yPosition to the maximum height used by either column
        yPosition = Math.max(leftFilterY, rightFilterY) + 5;
      }

      // Add Summary Statistics
      if (data.summary) {
        checkPageBreak(30);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Summary Statistics', margin, yPosition);
        yPosition += 8;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Total Wishlists: ${data.summary.total}`, margin, yPosition);
        yPosition += 6;
        pdf.text(`For Sale: ${data.summary.forSale}`, margin, yPosition);
        pdf.text(`For Rent: ${data.summary.forRent}`, margin + 60, yPosition);
        yPosition += 10;
      }

      // Export Chart 1: By Possession Type
      if (chart1Ref.current) {
        const canvas1 = await html2canvas(chart1Ref.current, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const imgData1 = canvas1.toDataURL('image/png');
        const imgWidth1 = pageWidth - 2 * margin;
        const imgHeight1 = (canvas1.height * imgWidth1) / canvas1.width;

        // Check if there's enough space for heading (8mm) + image
        checkPageBreak(8 + imgHeight1 + 10);
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('By Possession Type', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 8;

        // Center the image horizontally
        const imgX1 = (pageWidth - imgWidth1) / 2;
        pdf.addImage(imgData1, 'PNG', imgX1, yPosition, imgWidth1, imgHeight1);
        yPosition += imgHeight1 + 10;
      }

      // Export Chart 2: Top Locations
      if (chart2Ref.current) {
        const canvas2 = await html2canvas(chart2Ref.current, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const imgData2 = canvas2.toDataURL('image/png');
        const imgWidth2 = pageWidth - 2 * margin;
        const imgHeight2 = (canvas2.height * imgWidth2) / canvas2.width;

        // Check if there's enough space for heading (8mm) + image
        checkPageBreak(8 + imgHeight2 + 10);
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Top Locations', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 8;

        // Center the image horizontally
        const imgX2 = (pageWidth - imgWidth2) / 2;
        pdf.addImage(imgData2, 'PNG', imgX2, yPosition, imgWidth2, imgHeight2);
        yPosition += imgHeight2 + 10;
      }

      // Export Chart 3: Top Categories
      if (chart3Ref.current) {
        const canvas3 = await html2canvas(chart3Ref.current, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const imgData3 = canvas3.toDataURL('image/png');
        const imgWidth3 = pageWidth - 2 * margin;
        const imgHeight3 = (canvas3.height * imgWidth3) / canvas3.width;

        // Check if there's enough space for heading (8mm) + image
        checkPageBreak(8 + imgHeight3 + 10);
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Top Categories', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 8;

        // Center the image horizontally
        const imgX3 = (pageWidth - imgWidth3) / 2;
        pdf.addImage(imgData3, 'PNG', imgX3, yPosition, imgWidth3, imgHeight3);
        yPosition += imgHeight3 + 10;
      }

      // Export Chart 4: Top Sub Categories
      if (chart4Ref.current) {
        const canvas4 = await html2canvas(chart4Ref.current, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const imgData4 = canvas4.toDataURL('image/png');
        const imgWidth4 = pageWidth - 2 * margin;
        const imgHeight4 = (canvas4.height * imgWidth4) / canvas4.width;

        // Check if there's enough space for heading (8mm) + image
        checkPageBreak(8 + imgHeight4 + 10);
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Top Sub Categories', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 8;

        // Center the image horizontally
        const imgX4 = (pageWidth - imgWidth4) / 2;
        pdf.addImage(imgData4, 'PNG', imgX4, yPosition, imgWidth4, imgHeight4);
        yPosition += imgHeight4 + 10;
      }

      // Export Table
      if (allWishlistsData.length > 0) {
        checkPageBreak(30);
        
        // Title
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Wishlist Details', margin, yPosition);
        yPosition += 6;
        
        // Total count
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(25, 118, 210);
        pdf.text(`Total Wishlists: ${totalCount}`, margin, yPosition);
        yPosition += 8;

        // Table configuration - Adjusted for landscape A4 (297mm width, ~267mm usable with 15mm margins)
        const tableHeaders = ['S.No', 'Wishlist Name', 'User', 'Possession Type', 'Location', 'Category', 'Sub Category', 'Price Range', 'Created Date'];
        const colWidths = [12, 42, 32, 26, 40, 26, 30, 36, 30]; // Total: ~272mm (fits comfortably in landscape)
        const tableStartX = margin;
        const tableEndX = pageWidth - margin;
        const rowHeight = 10; // Increased from 8 to accommodate multiple items
        const headerHeight = 10;
        const cellPadding = 3;
        const headerY = yPosition;

        // Header background
        pdf.setFillColor(25, 118, 210);
        pdf.rect(tableStartX, headerY, tableEndX - tableStartX, headerHeight, 'F');

        // Header text - center aligned
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        let xPos = tableStartX;
        tableHeaders.forEach((header, index) => {
          // Use full header text, but truncate if too long for the column
          let headerText = header;
          const maxWidth = colWidths[index] - (cellPadding * 2);
          const textWidth = pdf.getTextWidth(headerText);
          if (textWidth > maxWidth) {
            // Truncate if needed
            while (pdf.getTextWidth(headerText + '...') > maxWidth && headerText.length > 0) {
              headerText = headerText.substring(0, headerText.length - 1);
            }
            headerText = headerText + '...';
          }
          const finalTextWidth = pdf.getTextWidth(headerText);
          const textX = xPos + (colWidths[index] / 2) - (finalTextWidth / 2); // Center align
          pdf.text(headerText, textX, headerY + 7);
          xPos += colWidths[index];
        });
        yPosition += headerHeight + 2;
        pdf.setTextColor(0, 0, 0);

        // Table rows
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        allWishlistsData.forEach((wishlist, index) => {
          // Format location - handle multiple locations (stack vertically)
          const locationCount = wishlist.locations?.length || 1;
          
          // Format sub-category - handle multiple sub-categories (stack vertically)
          const subCategoriesArray = (wishlist as any).subCategories && Array.isArray((wishlist as any).subCategories) && (wishlist as any).subCategories.length > 0
            ? (wishlist as any).subCategories.map((subCat: any) => typeof subCat === 'string' ? subCat : subCat.subCategoryName || subCat.name || subCat)
            : wishlist.subCategory ? [wishlist.subCategory] : ['N/A'];
          const subCategoryCount = subCategoriesArray.length;
          
          // Calculate row height based on content
          // Base height: 10mm, each location/sub-category adds 5mm
          // Use the maximum count to determine row height
          const maxCount = Math.max(locationCount, subCategoryCount);
          const currentRowHeight = maxCount > 1 ? rowHeight + ((maxCount - 1) * 5) : rowHeight;
          
          checkPageBreak(currentRowHeight + 2);
          
          const rowY = yPosition;
          
          // Alternate row background
          if (index % 2 === 0) {
            pdf.setFillColor(250, 250, 250);
            pdf.rect(tableStartX, rowY, tableEndX - tableStartX, currentRowHeight, 'F');
          }

          // Get locations array for vertical stacking
          const locationsArray = wishlist.locations && wishlist.locations.length > 0
            ? wishlist.locations.map((loc: any) => loc.locationName)
            : wishlist.location ? [wishlist.location] : ['Unknown'];

          // Row data
          const rowData = [
            (index + 1).toString(),
            (wishlist.name || 'N/A').substring(0, 25),
            (wishlist.userName || 'N/A').substring(0, 18),
            wishlist.possessionType || 'N/A',
            '', // Location will be handled separately below
            (wishlist.category || 'N/A').substring(0, 15),
            '', // Sub Category will be handled separately below
            `${wishlist.minPrice.toLocaleString()} - ${wishlist.maxPrice.toLocaleString()}`,
            new Date(wishlist.createdAt).toLocaleDateString('en-GB'),
          ];

          xPos = tableStartX;
          rowData.forEach((cell, cellIndex) => {
            // Special handling for location column (column index 4) - stack vertically
            if (cellIndex === 4) {
              // Print each location on a new line, starting from top of cell, center-aligned
              let currentY = rowY + 4; // Start from top of cell
              locationsArray.forEach((loc: string) => {
                const locationText = loc.substring(0, 25); // Truncate if too long
                const textWidth = pdf.getTextWidth(locationText);
                const textX = xPos + (colWidths[cellIndex] / 2) - (textWidth / 2); // Center align
                pdf.text(locationText, textX, currentY);
                currentY += 5; // Move down 5mm for next location
              });
            } else if (cellIndex === 6) {
              // Special handling for sub-category column (column index 6) - stack vertically, center-aligned
              // Print each sub-category on a new line, starting from top of cell
              let currentY = rowY + 4; // Start from top of cell
              subCategoriesArray.forEach((subCat: string) => {
                const subCategoryText = subCat.substring(0, 20); // Truncate if too long
                const textWidth = pdf.getTextWidth(subCategoryText);
                const textX = xPos + (colWidths[cellIndex] / 2) - (textWidth / 2); // Center align
                pdf.text(subCategoryText, textX, currentY);
                currentY += 5; // Move down 5mm for next sub-category
              });
            } else {
              // For other columns, center both horizontally and vertically
              const textWidth = pdf.getTextWidth(cell);
              const textX = xPos + (colWidths[cellIndex] / 2) - (textWidth / 2); // Center align horizontally
              const textY = rowY + (currentRowHeight / 2) + 2; // Center align vertically
              pdf.text(cell, textX, textY);
            }
            
            xPos += colWidths[cellIndex];
          });
          
          yPosition += currentRowHeight + 1;
        });
        
        // Draw outer border (top only, no sides or bottom border)
        pdf.setLineWidth(0.3);
        pdf.setDrawColor(180, 180, 180);
        // Top border only
        pdf.line(tableStartX, headerY, tableEndX, headerY);
        
        yPosition += 8;
      }

      // Generate filename with date range
      const filename = `Wishlist_Analytics_${startDate ? formatDateToYYYYMMDD(startDate) : 'N/A'}_to_${endDate ? formatDateToYYYYMMDD(endDate) : 'N/A'}.pdf`;

      // Save PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={<GetAppIcon />}
      onClick={handleExportPDF}
      disabled={exporting || !data || loading}
    >
      {exporting ? 'Exporting...' : 'Export PDF'}
    </Button>
  );
};

export default WishlistPdfExport;

